import { Divider } from '@design-system';
import { useTranslation } from 'next-i18next';
import { FC, useEffect, useMemo } from 'react';
import SbEditable from 'storyblok-react';

import { JOURNAL_PAGE_TYPE } from '../../constants';
import { AuthEntryPoint } from '../../features/auth/types';
import { FranchiseIndex, Franchises } from '../../types/Franchises';
import { GlossaryItem } from '../../types/Glossary';
import { TrackPageView } from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { AnalyticsObjectProvider } from '../../utils/analytics/object';
import { GlossaryLink } from '../../utils/glossary';
import { getAnalyticsMap, getWordCount } from '../../utils/journals/franchises';
import { readingTimeCalculator } from '../../utils/readingTimeCalculator';
import { ContentBlocks } from '../ContentBlocks';
import { GlossaryContextProvider } from '../Glossary/GlossaryContext';
import { GlossaryPills } from '../Glossary/GlossaryPills';
import PageSection from '../Layout/PageSection';
import { ArticleLd } from '../Schema/ArticleSchema';
import { SubscriptionBanner } from '../Subscriptions';
import { JournalsBreadcrumbs } from './JournalsBreadcrumbs';
import { JournalsHero } from './JournalsHero';
import { JournalsList } from './List/JournalsList';

interface JournalsPageProps {
  locale: string;
  franchise: string;
  franchiseGlossaryTerms: GlossaryLink[];
  franchiseIndex: FranchiseIndex | null;
  franchisePage: Franchises;
  franchiseStories: Franchises[];
  journalStories: Franchises[];
  glossary: GlossaryItem[];
}

export const JournalsPage: FC<JournalsPageProps> = ({
  franchise,
  franchiseGlossaryTerms,
  franchiseIndex,
  franchisePage,
  franchiseStories,
  glossary,
  journalStories,
  locale,
}) => {
  const { t } = useTranslation(['common']);

  const {
    article,
    author,
    date,
    heroLayoutVariant,
    heroPicker,
    poster,
    seo,
    subtitle,
    tag,
    title,
  } = franchisePage.content;

  const [media] = heroPicker || [];
  const wordCount = getWordCount(article);

  const [subscription] = franchiseIndex?.content.subscription || [];

  const properties = useMemo(
    () => getAnalyticsMap(locale, franchise, franchisePage),
    [locale, franchise, franchisePage],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(properties);

  useEffect(() => {
    TrackPageView({
      page: JOURNAL_PAGE_TYPE,
      title,
      properties,
    });
  }, [locale, title, properties]);

  return (
    <GlossaryContextProvider
      analytics={{
        context: 'insight article',
        page: JOURNAL_PAGE_TYPE,
      }}
      glossary={glossary}
    >
      <ArticleLd
        author={author}
        dateModified={franchisePage.published_at || ''}
        datePublished={date}
        description={seo.description}
        image={poster.filename}
        slug={franchisePage.full_slug}
        tag={tag}
        title={seo.title}
        wordCount={wordCount}
      />
      <JournalsBreadcrumbs
        basePath={{
          href: `/insights/${franchise}/1`,
          name: franchiseIndex?.content.title || franchise,
        }}
        handleClick={(item) => {
          analyticsEmitter('journal', 'breadcrumb click', { item });
        }}
        title={title}
      />
      {/* @ts-expect-error require storyblok-react upgrade */}
      <SbEditable content={franchisePage.content}>
        <JournalsHero
          author={author}
          handleShare={(platform) => {
            analyticsEmitter('journal', 'share click', { platform });
          }}
          handleVideoPlay={() => {
            analyticsEmitter('journal', 'video click');
          }}
          media={media}
          publishedAt={date}
          readingTime={readingTimeCalculator(wordCount)}
          subtitle={subtitle}
          title={title}
          variant={heroLayoutVariant}
        />
      </SbEditable>
      <div className="flex flex-col space-y-9 py-9 md:space-y-12 md:py-12">
        <PageSection>
          <AnalyticsObjectProvider name="journal" properties={properties}>
            {() => <ContentBlocks content={article} />}
          </AnalyticsObjectProvider>
          <GlossaryPills
            className="mt-12 md:mt-14"
            glossary={franchiseGlossaryTerms}
            onClick={(term) => {
              analyticsEmitter('journal', 'glossary term click', { term });
            }}
            title={t('common:tags')}
            titleIsBold
          />
        </PageSection>
        {franchiseStories.length > 0 && <Divider />}
        <PageSection>
          <JournalsList
            button={{
              handleClick: () => {
                analyticsEmitter('journal', 'view all click');
              },
              href: `/insights/${franchise}/1`,
              title: t('common:viewAll'),
            }}
            handleArticleClick={(item, index) => {
              analyticsEmitter('journal', 'article click', {
                position: index + 1,
                ...getAnalyticsMap(locale, franchise, item),
              });
            }}
            stories={franchiseStories}
            title={t('insights:discoverOther')}
          />
        </PageSection>
        {subscription && (
          <>
            <Divider />
            <PageSection>
              <SubscriptionBanner
                buttonLocation="insights post"
                image={subscription.image.filename}
                source={AuthEntryPoint.NewsletterInsights}
                subtitle={subscription.subtitle}
                title={subscription.title}
              />
            </PageSection>
          </>
        )}
        {journalStories.length > 0 && <Divider />}
        <PageSection>
          <JournalsList
            button={{
              handleClick: () => {
                analyticsEmitter('journal', 'view index click');
              },
              href: '/insights/1',
              title: t('common:viewAll'),
            }}
            handleArticleClick={(_, index) => {
              analyticsEmitter('discover more', 'article click', {
                position: index + 1,
              });
            }}
            stories={journalStories}
            title={t('insights:discoverMore')}
          />
        </PageSection>
      </div>
    </GlossaryContextProvider>
  );
};
